import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";

interface FormStatus {
  type: "idle" | "success" | "error";
  message: string;
}

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>({ type: "idle", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "success", message: "感谢您的反馈，我们会尽快与您联系。" });
      form.reset();
      return;
    }

    formData.delete("website_alt");
    setSubmitting(true);
    try {
      const response = await fetch("https://readdy.ai/api/form/db6cm5fqhq9pk5mecsh0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus({ type: "success", message: "提交成功！感谢您的反馈，我们会尽快与您联系。" });
        form.reset();
      } else {
        setStatus({
          type: "error",
          message: typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。",
        });
      }
    } catch {
      setStatus({ type: "error", message: "网络异常，提交失败，请稍后重试。" });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "h-12 w-full rounded-md border border-background-200 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "联系我们" }]} />

      <header className="mt-5 rounded-lg bg-primary-950 p-6 md:p-10">
        <span className="font-display text-xs tracking-[0.3em] text-primary-300">CONTACT</span>
        <h1 className="mt-2 text-2xl font-black text-background-50 md:text-4xl">联系我们</h1>
        <p className="mt-4 max-w-3xl text-sm leading-8 text-background-300 md:text-base">
          无论是内容建议、合作洽谈还是版权相关事宜，欢迎通过以下方式与我们取得联系，或直接填写右侧反馈表单，我们会认真阅读每一条留言。
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-bold text-foreground-950">联系方式</h2>
            <ul className="mt-4 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="font-medium text-foreground-900">邮箱</p>
                  <p className="text-foreground-600">hello@passionvideo.com</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-phone-line" />
                </span>
                <div>
                  <p className="font-medium text-foreground-900">电话</p>
                  <p className="text-foreground-600">400-888-2026（工作日 9:00-18:00）</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-map-pin-line" />
                </span>
                <div>
                  <p className="font-medium text-foreground-900">地址</p>
                  <p className="text-foreground-600">上海市静安区光影文化产业园 A 座</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
            <h2 className="text-base font-bold text-foreground-950">关注我们</h2>
            <div className="mt-4 flex gap-3">
              {[
                { icon: "ri-weibo-fill", label: "微博" },
                { icon: "ri-wechat-fill", label: "微信" },
                { icon: "ri-qq-fill", label: "QQ" },
                { icon: "ri-movie-line", label: "影视号" },
              ].map((s) => (
                <span
                  key={s.label}
                  title={s.label}
                  className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700"
                >
                  <i className={`${s.icon} text-xl`} />
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
          >
            <h2 className="text-lg font-bold text-foreground-950">反馈表单</h2>
            <p className="mt-1 text-sm text-foreground-500">带 * 为必填项，我们会妥善保管你的信息。</p>

            <div className="mt-5 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="contact-name">
                    姓名 *
                  </label>
                  <input id="contact-name" name="name" required placeholder="请输入您的姓名" className={inputClass} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="contact-email">
                    邮箱 *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入您的邮箱"
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="contact-message">
                  留言内容 *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="请描述您的建议或问题（不超过 500 字）"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 resize-none"
                />
              </div>

              <input
                className="field-aux-note"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                  {submitting ? "提交中..." : "提交反馈"}
                </button>
                <p className="text-xs text-foreground-400">提交后我们将在 1-2 个工作日内回复。</p>
              </div>

              {status.type === "success" && (
                <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line text-lg" />
                  {status.message}
                </p>
              )}
              {status.type === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line text-lg" />
                  {status.message}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}